import React, { useEffect, useRef, useState } from 'react';
import { ArrowDownTrayIcon, CheckIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { EDashboard } from '../../types';
import {
  captureElementAsPng,
  getDashboardElement,
  generateFileName,
  downloadFile
} from '../../utils/export-helpers';

interface ExportPngButtonProps {
  activeDashboard: EDashboard;
  projectName?: string;
}

type ExportStatus = 'idle' | 'exporting' | 'done' | 'error';

export const ExportPngButton: React.FC<ExportPngButtonProps> = ({
  activeDashboard,
  projectName
}) => {
  const [status, setStatus] = useState<ExportStatus>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const settle = (result: 'done' | 'error') => {
    setStatus(result);
    timerRef.current = setTimeout(() => setStatus('idle'), 2500);
  };

  const handleExportPng = async () => {
    if (status === 'exporting') return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus('exporting');

    try {
      const dashboardElement = getDashboardElement();

      if (!dashboardElement) {
        console.error('Dashboard content element not found');
        settle('error');
        return;
      }

      const dataUrl = await captureElementAsPng(dashboardElement);
      const filename = generateFileName(projectName, activeDashboard, 'png');
      downloadFile(dataUrl, filename);
      settle('done');
    } catch (error) {
      console.error('Error exporting dashboard as PNG:', error);
      settle('error');
    }
  };

  return (
    <button
      type="button"
      onClick={handleExportPng}
      disabled={status === 'exporting'}
      className="btn-icon flex items-center justify-center gap-2 min-w-[8.5rem]"
      title={status === 'error' ? 'Failed to export dashboard as PNG. Try again.' : 'Export dashboard as PNG'}
    >
      {status === 'done' ? (
        <CheckIcon className="h-4 w-4 text-primary" />
      ) : status === 'error' ? (
        <ExclamationCircleIcon className="h-4 w-4 text-red-400" />
      ) : (
        <ArrowDownTrayIcon className="h-4 w-4" />
      )}
      {status === 'exporting'
        ? 'Exporting...'
        : status === 'done'
        ? 'Exported'
        : status === 'error'
        ? 'Export failed'
        : 'Export PNG'}
    </button>
  );
};
